import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { fetchLegalContent, LegalType } from '../api/legalContent';
import { ceCertificateUrl } from '../utils/ceCertificateUrl';
import Logo from './Logo';
import FooterLogo from './FooterLogo';
import { LocaleContext } from '../contexts/LocaleContext';
import { Locale } from '../i18n';
import { colors, gradients, shadows, fontSize, fontWeight, spacing, buttonDefaults } from '../style/tokens';
import { headerBg } from '../style/header';
import welcomeBg from '../assets/images/welcome-bg.jpg';
import slide2 from '../assets/images/slide-2.png';
import slide3 from '../assets/images/slide-3.png';
import { useTheme } from 'styled-components';
import { BrandingContext } from './shared/BrandingContext';
import { decodeJwt } from '../utils/decodeJwt';

// ── Slide data ────────────────────────────────────────────────────────────────

const DEFAULT_SLIDES = [
  { img: welcomeBg, overlay: 'rgba(30,60,20,0.08)', imgStyle: {}, titleKey: 'slides.1.title', subKey: 'slides.1.sub' },
  { img: slide3, overlay: 'rgba(0,0,0,0.10)', imgStyle: { objectPosition: 'center 10%', filter: 'brightness(0.88) contrast(1.05)' }, titleKey: 'slides.2.title', subKey: 'slides.2.sub' },
  { img: slide2, overlay: 'rgba(20,20,40,0.15)', imgStyle: { objectPosition: '60% 20%', filter: 'brightness(0.82) contrast(1.05)' }, titleKey: 'slides.3.title', subKey: 'slides.3.sub' },
];


const LOCALES: { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'ar', label: 'العربية' },
  { code: 'lv', label: 'Latviešu' },
  { code: 'pt', label: 'Português' },
  { code: 'ja', label: '日本語' },
];

// ── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: var(--sfondo, ${colors.bg});
  font-family: 'Outfit', sans-serif;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 26px;
  min-height: 82px;
  flex-shrink: 0;
  position: relative;
  background: ${headerBg};
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const LangDropdown = styled.div`
  position: absolute;       /* ← was: relative */
  top: -33px;               /* ← lifted 33px above SlideTextWrapper to clear Title */
  right: 0;                 /* ← add: pushes to right edge of SlideTextWrapper */
  z-index: 20;
`;

const LangBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1.5px solid #ddd;
  border-radius: 20px;
  background: ${colors.bg};
  color: ${colors.navy};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.04em;
  cursor: pointer;
  font-family: 'Outfit', sans-serif;
`;

const LangMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: ${colors.bg};
  border: 1.5px solid #ddd;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
  min-width: 64px;
`;

const LangOption = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 14px;
  text-align: left;
  border: none;
  background: ${({ active }) => active ? '#f0f9e6' : 'transparent'};
  color: ${({ active }) => active ? colors.green : colors.navy};
  font-size: ${fontSize.md};
  font-weight: ${({ active }) => active ? fontWeight.bold : fontWeight.medium};
  font-family: 'Outfit', sans-serif;
  cursor: pointer;
  @media (hover: hover) { &:hover { background: #f5f5f5; } }
`;

// ── Carousel ──────────────────────────────────────────────────────────────────

const Hero = styled.section`
  width: 100%;
  aspect-ratio: 1 / 0.6;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
`;

const HeroPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1 / 0.6;
  flex-shrink: 0;
  background: #f0f0f0;
`;



const HeroTrack = styled.div<{ current: number; dragging: boolean }>`
  display: flex;
  width: ${DEFAULT_SLIDES.length * 100}%;
  height: 100%;
  transform: translateX(${({ current }) => (-current * 100) / DEFAULT_SLIDES.length}%);
  transition: ${({ dragging }) => (dragging ? 'none' : 'transform 0.35s cubic-bezier(.4,0,.2,1)')};
`;

const HeroSlide = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  draggable: false;
`;

const HeroOverlay = styled.div<{ bg: string }>`
  position: absolute;
  inset: 0;
  background: ${({ bg }) => bg};
`;

// ── Bottom content ────────────────────────────────────────────────────────────

const Content = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${spacing.xl} ${spacing.xl2} ${spacing.xl2};
`;

const Dots = styled.div`
  display: flex;
  gap: 7px;
  margin-bottom: 14px;
  justify-content: center;
  align-items: center;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${({ active }) => (active ? '22px' : '7px')};
  height: 7px;
  border-radius: 4px;
  background: ${({ active, theme }) => (active
    ? (theme.coloreBottoneAttivo || buttonDefaults.active)
    : (theme.coloreBottoneInattivo || buttonDefaults.inactive))};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;
  @media (hover: hover) {
    &:hover {
      background: ${({ active, theme }) => (active
        ? (theme.coloreBottoneAttivo || buttonDefaults.active)
        : (theme.coloreBottoneInattivo || buttonDefaults.inactive))};
    }
  }
`;

const SlideTextWrapper = styled.div`
  min-height: 82px;
  margin-bottom: 4px;
  position: relative;       /* ← add */
`;

const Title = styled.h1<{ visible: boolean }>`
  font-size: clamp(24px, 6.5vw, 32px);
  font-weight: ${fontWeight.extrabold};
  color: ${colors.text};
  line-height: 1.2;
  margin-bottom: 10px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.18s ease;
`;

const SubText = styled.p<{ visible: boolean }>`
  font-size: ${fontSize.base};
  color: ${colors.muted};
  line-height: 1.65;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.18s ease;
`;

const PrivacyList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const PrivacyItem = styled.li`
  font-size: ${fontSize.xs};
  color: #999;
  line-height: 1.5;
  padding-left: 14px;
  position: relative;
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${p => p.theme.gradient1};
  }
`;

const TermsNotice = styled.div`
  margin-top: ${spacing.md};
  font-size: ${fontSize.xs};
  color: #999;
  line-height: 1.55;
`;

const TermsLabel = styled.span`
  display: block;
  font-weight: ${fontWeight.bold};
  color: #bbb;
  margin-bottom: 2px;
  text-transform: uppercase;
  font-size: ${fontSize.xxs};
  letter-spacing: 0.04em;
`;

const CeRegistration = styled.div`
  margin-top: 6px;
  a {
    color: ${colors.navy};
    text-decoration: underline;
  }
`;

// ── Consent checkboxes ────────────────────────────────────────────────────────

const Consents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: ${spacing.xl} 0 0;
`;

const ConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CustomBox = styled.span<{ checked: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 1.5px solid ${({ checked, theme }) => (checked ? (theme.coloreBottoneInattivo || theme.gradient1) : '#d0d0d0')};
  background: ${({ checked, theme }) => (checked ? (theme.coloreBottoneInattivo || theme.gradient1) : '#fff')};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, background 0.15s;
  &::after {
    content: '';
    display: block;
    width: 5px;
    height: 9px;
    border: 2px solid #fff;
    border-top: none;
    border-left: none;
    transform: rotate(45deg) translate(-1px, -1px);
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transition: opacity 0.1s;
  }
`;

const ConsentText = styled.span`
  font-size: ${fontSize.md};
  color: #555;
  line-height: 1.5;
  a {
    color: ${colors.navy};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

// ── Buttons ───────────────────────────────────────────────────────────────────

const Disclaimer = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  text-align: center;
  margin: 14px 0 10px;
  line-height: 1.55;
`;

// Replaces the Disclaimer's spacing when it's hidden (CE build) so the button
// below isn't flush against the consents.
const DisclaimerSpacer = styled.div`
  height: 20px;
`;

const BtnNext = styled.button<{ enabled: boolean }>`
  display: block;
  width: 100%;
  padding: 18px 0;
  border-radius: 50px;
  border: none;
  background: ${({ enabled, theme }) => enabled
    ? (theme.coloreBottoneInattivo || buttonDefaults.inactive)
    : (theme.coloreBottoneDisabilitato || '#c8c8c8')};
  color: #fff;
  font-size: 17px;
  font-weight: ${fontWeight.bold};
  font-family: 'Outfit', sans-serif;
  text-align: center;
  cursor: ${({ enabled }) => (enabled ? 'pointer' : 'not-allowed')};
  transition: background 0.15s, transform 0.1s;
  @media (hover: hover) { &:hover { background: ${({ enabled, theme }) => enabled
    ? (theme.coloreBottoneAttivo || buttonDefaults.active)
    : (theme.coloreBottoneDisabilitato || '#c8c8c8')}; } }
  &:active { background: ${({ enabled, theme }) => enabled
    ? (theme.coloreBottoneAttivo || buttonDefaults.active)
    : (theme.coloreBottoneDisabilitato || '#c8c8c8')}; transform: ${({ enabled }) => (enabled ? 'scale(0.98)' : 'none')}; }
`;


const DevPreviewButton = styled.button`
  display: block;
  margin: 12px auto 0;
  background: none;
  border: 1px dashed #ccc;
  color: #bbb;
  font-size: ${fontSize.xs};
  font-family: 'Outfit', sans-serif;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  @media (hover: hover) { &:hover { color: #888; border-color: #aaa; } }
`;

// ── Modal ─────────────────────────────────────────────────────────────────────

// Portaled to document.body (see the two ModalBackdrop render sites below), so
// this is fixed to the true viewport — not absolute within Page's own
// internally-scrolling box. Page can be long (this is a bottom-of-page link)
// and scrolled by the time the modal opens; position:absolute here previously
// meant the whole sheet (including its header) could render already shifted
// above the visible viewport along with however far Page had scrolled.
const ModalBackdrop = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: flex-start;
  z-index: 100;
`;

const ModalSheet = styled.div`
  background: #fff;
  border-radius: 0 0 20px 20px;
  width: 100%;
  max-width: 450px;
  max-height: 80vh;
  max-height: 80svh; /* stable on iOS: small-viewport height doesn't grow when Safari's toolbar hides */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* NOTE: the header below is a plain flex child, NOT position: sticky.
     #root (style/global.ts) carries -webkit-transform: translate3d(0,0,0)
     for GPU compositing; a transform on ANY ancestor breaks position:
     sticky/fixed descendants (changes what their containing block is),
     which is why a sticky header here rendered inert on iOS and was pushed
     off-screen entirely on Android. A fixed flex header + independently
     scrolling body sidesteps sticky positioning altogether. */
`;

// Static top banner: a fixed-size flex child; ModalBody scrolls independently
// beneath it (see the note on ModalSheet for why this isn't position: sticky).
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
`;

const ModalTitle = styled.span`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  color: ${colors.text};
`;

const ModalClose = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  color: #666;
  font-size: ${fontSize.base};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0; /* lets this child shrink below its content size so it can scroll instead of overflowing ModalSheet */
  overflow-y: auto;
  overscroll-behavior: contain; /* stops the scroll from chaining to the page behind (iOS jump) */
  padding: ${spacing.lg} ${spacing.xl} 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ModalSection = styled.div``;

const ModalSectionTitle = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.theme.gradient1};
  margin-bottom: ${spacing.sm};
`;

const ModalText = styled.p`
  font-size: ${fontSize.md};
  color: ${colors.muted};
  line-height: 1.6;
  a { color: ${colors.navy}; }
`;

const ModalList = styled.ul`
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ModalListItem = styled.li`
  font-size: ${fontSize.md};
  color: ${colors.muted};
  line-height: 1.5;
`;

// Renders the first-party legal HTML returned by the legal API.
const LegalHtml = styled.div`
  font-size: ${fontSize.md};
  color: ${colors.text};
  line-height: 1.6;
  h1 { font-size: 20px; font-weight: 700; margin: 0 0 12px; }
  h2 { font-size: 17px; font-weight: 700; margin: 18px 0 8px; }
  h3 { font-size: 15px; font-weight: 600; margin: 14px 0 6px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 10px; padding-left: 20px; }
  li { margin: 0 0 5px; }
  a { color: ${colors.navy}; }
  strong { font-weight: 600; }
`;

// Full-page CE certificate (certificato_ce_webapp.php) shown inline in a modal.
// It's a standalone first-party page, not a JSON fragment like terms/privacy,
// so it's embedded via iframe rather than injected HTML.
//
// Give the sheet a DEFINITE height (not just max-height): a cross-origin iframe
// has no measurable content height, so with only a max-height the sheet would
// collapse to the iframe's default ~150px. A fixed height lets the iframe below
// the header flex to fill it, matching the tall terms/privacy sheet.
const CeModalSheet = styled(ModalSheet)`
  height: 90vh;
  height: 90svh;
  max-height: 90svh;
`;

const CeFrame = styled.iframe`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border: 0;
`;

type LegalState = { loading: boolean; error: boolean; html: string | null };

/**
 * Fetches legal HTML for (type, lang) when `open` becomes true, and re-fetches
 * if the language changes while open. Content comes from the legal API.
 */
function useLegalContent(type: LegalType, lang: string, open: boolean): LegalState {
  const [state, setState] = useState<LegalState>({ loading: false, error: false, html: null });
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState({ loading: true, error: false, html: null });
    fetchLegalContent(type, lang)
      .then(d => { if (!cancelled) setState({ loading: false, error: false, html: d.content }); })
      .catch(() => { if (!cancelled) setState({ loading: false, error: true, html: null }); });
    return () => { cancelled = true; };
  }, [type, lang, open]);
  return state;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface WelcomePageProps {
  onContinue: () => void;
  onPreviewReport?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const WelcomePage: React.FC<WelcomePageProps> = ({ onContinue, onPreviewReport }) => {
  const intl = useIntl();
  const { locale, setLocale } = useContext(LocaleContext);
  const [langOpen, setLangOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [chkTc, setChkTc] = useState(false);
  const [chkPp, setChkPp] = useState(false);
  const [tcOpen, setTcOpen] = useState(false);
  const [ppOpen, setPpOpen] = useState(false);
  const [ceOpen, setCeOpen] = useState(false);
  const termsContent = useLegalContent('termini', locale, tcOpen);
  const privacyContent = useLegalContent('privacy', locale, ppOpen);
  const agreed = chkTc && chkPp;

  // Lock the background page's own scroll while a legal modal is open, so it
  // can't be scrolled further out from under the (now viewport-fixed) modal.
  useEffect(() => {
    if (!tcOpen && !ppOpen && !ceOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [tcOpen, ppOpen, ceOpen]);

  const dragRef = useRef({ isDragging: false, startX: 0, delta: 0, wasDrag: false });
  const trackRef = useRef<HTMLDivElement>(null);

  const { carouselImages, istanza_ce, enabledMetrics } = useTheme();
  const { isLoadingToken, rawToken } = useContext(BrandingContext);

  // useEffect(() => {
  //   if (rawToken && process.env.NODE_ENV === 'development') {
  //     // eslint-disable-next-line no-console
  //     console.log('[WelcomePage] decoded JWT:', decodeJwt(rawToken));
  //   }
  // }, [rawToken]);
  const SLIDES = DEFAULT_SLIDES.map((s, i) =>
    carouselImages[i] ? { ...s, img: carouselImages[i] as string } : s
  );

  // The first slide carries the product name. CE instances are comestai.Check;
  // a non-CE partner that opted Burnout (§4.10) into enabled_metrics gets
  // comestai.Burnout; everyone else gets comestai.Health.
  const burnoutEnabled = !!enabledMetrics?.includes('burnout');
  const slideTitleKey = (key: string) => {
    if (key !== 'slides.1.title') return key;
    if (istanza_ce) return 'slides.1.titleCe';
    if (burnoutEnabled) return 'slides.1.titleBurnout';
    return key;
  };

  const goTo = useCallback((index: number) => {
    const next = ((index % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setTextVisible(false);
    setTimeout(() => {
      setCurrent(next);
      setTextVisible(true);
    }, 180);
  }, []);

  // touch
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    dragRef.current.startX = e.touches[0].clientX;
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - dragRef.current.startX;
    if (Math.abs(dx) > 40) goTo(dx < 0 ? current + 1 : current - 1);
  }, [current, goTo]);

  const onHeroClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (dragRef.current.wasDrag) { dragRef.current.wasDrag = false; return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const mid = rect.left + rect.width / 2;
    goTo(e.clientX >= mid ? current + 1 : current - 1);
  }, [current, goTo]);

  const slide = SLIDES[current];

  return (
    <Page>
      <TopBar>
        <span style={{ width: 42 }} />
        <LogoCenter><Logo /></LogoCenter>
        <span style={{ width: 42 }} />
      </TopBar>

      {isLoadingToken ? (
        <HeroPlaceholder />
      ) : (
        <Hero
          aria-label="Onboarding slides"
          onClick={onHeroClick}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <HeroTrack ref={trackRef} current={current} dragging={false}>
            {SLIDES.map((s, i) => (
              <HeroSlide key={i}>
                <HeroImg src={s.img} alt="" draggable={false} style={s.imgStyle as React.CSSProperties} />
                <HeroOverlay bg={s.overlay} />
              </HeroSlide>
            ))}
          </HeroTrack>
        </Hero>
      )}

      <Content>
        <Dots role="tablist" aria-label="Slide navigation">
          {SLIDES.map((_, i) => (
            <Dot
              key={i}
              active={i === current}
              role="tab"
              aria-selected={i === current}
              onClick={e => { e.stopPropagation(); goTo(i); }}
            />
          ))}
        </Dots>

        <SlideTextWrapper>
          <LangDropdown>
            <LangBadge onClick={() => setLangOpen(o => !o)}>
              {LOCALES.find(l => l.code === locale)?.label ?? locale.toUpperCase()} <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
            </LangBadge>
            {langOpen && (
              <LangMenu>
                {LOCALES.map(l => (
                  <LangOption key={l.code} active={l.code === locale} onClick={() => { setLocale(l.code); setLangOpen(false); }}>
                    {l.label}
                  </LangOption>
                ))}
              </LangMenu>
            )}
          </LangDropdown>
          <Title visible={textVisible}>
            {intl.formatMessage({ id: slideTitleKey(slide.titleKey) })}
          </Title>
          <SubText visible={textVisible}>
            {intl.formatMessage({ id: slide.subKey })}
          </SubText>
        </SlideTextWrapper>

        <PrivacyList>
          <PrivacyItem>{intl.formatMessage({ id: 'welcome.privacyItem1' })}</PrivacyItem>
          <PrivacyItem>{intl.formatMessage({ id: 'welcome.privacyItem2' })}</PrivacyItem>
          <PrivacyItem>{intl.formatMessage({ id: 'welcome.privacyItem3' })}</PrivacyItem>
          <PrivacyItem>{intl.formatMessage({ id: 'welcome.privacyItem4' })}</PrivacyItem>
          <PrivacyItem>{intl.formatMessage({ id: 'welcome.privacyItem5' })}</PrivacyItem>
        </PrivacyList>

        <TermsNotice>
          <TermsLabel>{intl.formatMessage({ id: 'welcome.termsNoticeLabel' })}</TermsLabel>
          {intl.formatMessage({ id: istanza_ce ? 'welcome.termsNoticeCe' : 'welcome.termsNotice' })}
          {istanza_ce && (
            <CeRegistration>
              <a href="#" onClick={e => { e.preventDefault(); setCeOpen(true); }}>
                {intl.formatMessage({ id: 'welcome.termsLinkNoticeTextCe' })}
              </a>
            </CeRegistration>
          )}
        </TermsNotice>

        <Consents style={{ marginTop: 'auto', paddingTop: 18 }}>
          <ConsentLabel>
            <HiddenCheckbox type="checkbox" checked={chkTc} onChange={e => setChkTc(e.target.checked)} />
            <CustomBox checked={chkTc} />
            <ConsentText>
              {intl.formatMessage({ id: 'welcome.consentTerms' })}{' '}
              <a href="#" onClick={e => { e.preventDefault(); setTcOpen(true); }}>{intl.formatMessage({ id: 'welcome.termsLinkText' })}</a>.
            </ConsentText>
          </ConsentLabel>

          <ConsentLabel>
            <HiddenCheckbox type="checkbox" checked={chkPp} onChange={e => setChkPp(e.target.checked)} />
            <CustomBox checked={chkPp} />
            <ConsentText>
              {intl.formatMessage({ id: 'welcome.consentPrivacy' })}{' '}
              <a href="#" onClick={e => { e.preventDefault(); setPpOpen(true); }}>{intl.formatMessage({ id: 'welcome.privacyLinkTextShort' })}</a>.
            </ConsentText>
          </ConsentLabel>
        </Consents>

        {istanza_ce ? (
          <DisclaimerSpacer />
        ) : (
          <Disclaimer>{intl.formatMessage({ id: 'welcome.disclaimer' })}</Disclaimer>
        )}

        <BtnNext enabled={agreed} onClick={agreed ? onContinue : undefined}>
          {intl.formatMessage({ id: 'welcome.agreeAndContinue' })}
        </BtnNext>

        {onPreviewReport && process.env.NODE_ENV === 'development' && (
          <DevPreviewButton onClick={onPreviewReport}>
            [dev] preview report
          </DevPreviewButton>
        )}
      </Content>

      <FooterLogo />

      {/* T&C Modal — portaled to document.body: see the ModalBackdrop comment
          for why (Page can be scrolled when this opens; a portal keeps the
          modal fixed to the true viewport instead of Page's own scroll box). */}
      {createPortal(
        <ModalBackdrop open={tcOpen} onClick={e => { if (e.target === e.currentTarget) setTcOpen(false); }}>
          <ModalSheet>
            <ModalHeader>
              <ModalTitle>{intl.formatMessage({ id: 'welcome.modal.termsTitle' })}</ModalTitle>
              <ModalClose onClick={() => setTcOpen(false)}>✕</ModalClose>
            </ModalHeader>
            <ModalBody>
              {termsContent.loading && <ModalText>{intl.formatMessage({ id: 'welcome.modal.legalLoading' })}</ModalText>}
              {termsContent.error && <ModalText>{intl.formatMessage({ id: 'welcome.modal.legalError' })}</ModalText>}
              {termsContent.html && <LegalHtml dangerouslySetInnerHTML={{ __html: termsContent.html }} />}
            </ModalBody>
          </ModalSheet>
        </ModalBackdrop>,
        document.body,
      )}

      {/* Privacy Policy Modal — portaled, see above. */}
      {createPortal(
        <ModalBackdrop open={ppOpen} onClick={e => { if (e.target === e.currentTarget) setPpOpen(false); }}>
          <ModalSheet>
            <ModalHeader>
              <ModalTitle>{intl.formatMessage({ id: 'welcome.modal.privacyTitle' })}</ModalTitle>
              <ModalClose onClick={() => setPpOpen(false)}>✕</ModalClose>
            </ModalHeader>
            <ModalBody>
              {privacyContent.loading && <ModalText>{intl.formatMessage({ id: 'welcome.modal.legalLoading' })}</ModalText>}
              {privacyContent.error && <ModalText>{intl.formatMessage({ id: 'welcome.modal.legalError' })}</ModalText>}
              {privacyContent.html && <LegalHtml dangerouslySetInnerHTML={{ __html: privacyContent.html }} />}
            </ModalBody>
          </ModalSheet>
        </ModalBackdrop>,
        document.body,
      )}

      {/* CE Certificate Modal — portaled, see above. The per-language certificate
          page is embedded via iframe; ?lang follows the active app locale. */}
      {createPortal(
        <ModalBackdrop open={ceOpen} onClick={e => { if (e.target === e.currentTarget) setCeOpen(false); }}>
          <CeModalSheet>
            <ModalHeader>
              <ModalTitle>{intl.formatMessage({ id: 'welcome.modal.ceTitle' })}</ModalTitle>
              <ModalClose onClick={() => setCeOpen(false)}>✕</ModalClose>
            </ModalHeader>
            {ceOpen && (
              <CeFrame
                src={ceCertificateUrl(locale)}
                title={intl.formatMessage({ id: 'welcome.modal.ceTitle' })}
              />
            )}
          </CeModalSheet>
        </ModalBackdrop>,
        document.body,
      )}
    </Page>
  );
};

export default WelcomePage;
